import type { Calendar as PrimeCalendar, CalendarSelectionMode } from "primereact/calendar";
import { classNames } from "primereact/utils";
import { useMemo } from "react";
import type { ForwardedRef } from "react";

import "../../locale/prime-locale";
import { useStableRest } from "../../utils/use-stable-rest";
import { withDefaults } from "../../utils/with-defaults";
import { primeIcon } from "../icon";
import panelStyles from "./calendar.module.scss";
import type { CalendarProps, CalendarViewModel } from "./types-calendar";
import { useCalendarInputAria } from "./use-calendar-aria";

/** Ícones do Figma (14) no lugar dos do Prime: calendário do botão e setas do cabeçalho do painel. */
const FIGMA_DEFAULTS = {
  showIcon: true,
  icon: primeIcon("calendar", 14),
  prevIcon: primeIcon("chevron-left", 14),
  nextIcon: primeIcon("chevron-right", 14),
  dateFormat: "dd/mm/yy",
};

/**
 * ViewModel do Calendar: padrões do Figma (ícones, placeholder, barra Hoje/Limpar), `id` → `inputId`, `aria-*`
 * no `<input>` e `invalid` por `aria-invalid`; o `ref` da tela segue para a View como `calendarRef`; sem JSX.
 */
export function useCalendar(
  props: CalendarProps<CalendarSelectionMode>,
  ref: ForwardedRef<PrimeCalendar<CalendarSelectionMode>>,
): CalendarViewModel {
  const {
    id,
    inputId,
    invalid,
    panelClassName,
    "aria-label": ariaLabel,
    "aria-describedby": ariaDescribedBy,
    "aria-invalid": ariaInvalid,
    "aria-required": ariaRequired,
    inputRef: formInputRef,
    ...rest
  } = props;
  const stableRest = useStableRest(rest);
  // Compõe o `inputRef` da tela com o do DS: o aria do campo precisa do elemento, a tela também.
  const inputRef = useCalendarInputAria(
    {
      "aria-describedby": ariaDescribedBy,
      "aria-invalid": ariaInvalid,
      "aria-required": ariaRequired,
    },
    formInputRef,
  );
  const isInvalid = invalid || ariaInvalid === true || ariaInvalid === "true";

  const calendarProps = useMemo(
    () => ({
      ...withDefaults(
        {
          ...FIGMA_DEFAULTS,
          placeholder:
            stableRest.selectionMode === "range" ? "dd/mm/aaaa – dd/mm/aaaa" : "dd/mm/aaaa",
          // Barra "Hoje / Limpar" do Figma ligada por padrão; o frame Type=Inline (109:10138) não a desenha.
          showButtonBar: !stableRest.inline,
        },
        stableRest,
      ),
      ariaLabel: ariaLabel ?? stableRest.ariaLabel,
      // Ligações do DS: `inputId` liga o `<label>` do `FormField` e `inputRef` alimenta o aria do campo.
      inputId: inputId ?? id,
      inputRef,
      invalid: isInvalid,
      panelClassName: classNames(panelStyles.panel, panelClassName),
    }),
    [stableRest, ariaLabel, inputId, id, inputRef, isInvalid, panelClassName],
  );

  return { calendarRef: ref, calendarProps: calendarProps as CalendarViewModel["calendarProps"] };
}
